import React, { useEffect, useRef } from 'react';

interface Trace {
  startX: number;
  startY: number;
  cornerX: number;
  cornerY: number;
  endX: number;
  endY: number;
  length1: number;
  length2: number;
  totalLength: number;
}

interface Pulse {
  traceIndex: number;
  progress: number;
  speed: number;
}

interface Gear {
  x: number;
  y: number;
  radius: number;
  teeth: number;
  rotation: number;
  rotationSpeed: number;
}

interface BinaryDigit {
  x: number;
  y: number;
  speed: number;
  value: string;
  size: number;
  alpha: number;
}

const GRID_SPACING = 28;
const TRACE_COUNT = 14;
const PULSE_COUNT = 22;
const GEAR_COUNT = 5;
const DIGIT_COUNT = 14;

const RobotBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotionQuery =
      typeof window !== 'undefined'
        ? window.matchMedia?.('(prefers-reduced-motion: reduce)')
        : undefined;
    let reducedMotion = reducedMotionQuery?.matches ?? false;

    let traces: Trace[] = [];
    let pulses: Pulse[] = [];
    let gears: Gear[] = [];
    let digits: BinaryDigit[] = [];

    function buildTrace(): Trace {
      const startX = Math.floor(Math.random() * (canvas.width / GRID_SPACING)) * GRID_SPACING;
      const startY = Math.floor(Math.random() * (canvas.height / GRID_SPACING)) * GRID_SPACING;
      const horizontalFirst = Math.random() > 0.5;
      const length1 = (Math.floor(Math.random() * 8) + 4) * GRID_SPACING * (Math.random() > 0.5 ? 1 : -1);
      const length2 = (Math.floor(Math.random() * 8) + 4) * GRID_SPACING * (Math.random() > 0.5 ? 1 : -1);
      const cornerX = horizontalFirst ? startX + length1 : startX;
      const cornerY = horizontalFirst ? startY : startY + length1;
      const endX = horizontalFirst ? cornerX : cornerX + length2;
      const endY = horizontalFirst ? cornerY + length2 : cornerY;
      const absLen1 = Math.abs(length1);
      const absLen2 = Math.abs(length2);
      return {
        startX,
        startY,
        cornerX,
        cornerY,
        endX,
        endY,
        length1: absLen1,
        length2: absLen2,
        totalLength: absLen1 + absLen2,
      };
    }

    function initScene() {
      traces = Array.from({ length: TRACE_COUNT }, () => buildTrace());
      pulses = Array.from({ length: PULSE_COUNT }, () => ({
        traceIndex: Math.floor(Math.random() * traces.length),
        progress: Math.random(),
        speed: 0.0015 + Math.random() * 0.0025,
      }));
      gears = Array.from({ length: GEAR_COUNT }, (_, i) => {
        const radius = 60 + Math.random() * 60;
        let x: number;
        let y: number;
        if (i < 4) {
          const left = i % 2 === 0;
          const top = i < 2;
          x = left ? radius * 0.6 : canvas.width - radius * 0.6;
          y = top ? radius * 0.6 : canvas.height - radius * 0.6;
        } else {
          x = canvas.width / 2;
          y = canvas.height / 2 + (Math.random() - 0.5) * canvas.height * 0.3;
        }
        return {
          x,
          y,
          radius,
          teeth: 10 + Math.floor(Math.random() * 6),
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() * 0.003 + 0.001) * (Math.random() > 0.5 ? 1 : -1),
        };
      });
      digits = Array.from({ length: DIGIT_COUNT }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        speed: 0.2 + Math.random() * 0.5,
        value: Math.random() > 0.5 ? '1' : '0',
        size: 12 + Math.random() * 8,
        alpha: 0.15 + Math.random() * 0.2,
      }));
    }

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initScene();
      if (reducedMotion) {
        renderStatic();
      }
    };

    function drawBase() {
      const bg = ctx.createLinearGradient(0, 0, 0, canvas.height);
      bg.addColorStop(0, '#0a1220');
      bg.addColorStop(1, '#162032');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    function drawGrid() {
      ctx.strokeStyle = 'rgba(56, 189, 248, 0.08)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = 0; x <= canvas.width; x += GRID_SPACING) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
      }
      for (let y = 0; y <= canvas.height; y += GRID_SPACING) {
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
      }
      ctx.stroke();
    }

    function drawTraces() {
      ctx.strokeStyle = 'rgba(56, 189, 248, 0.35)';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      traces.forEach((t) => {
        ctx.beginPath();
        ctx.moveTo(t.startX, t.startY);
        ctx.lineTo(t.cornerX, t.cornerY);
        ctx.lineTo(t.endX, t.endY);
        ctx.stroke();

        ctx.fillStyle = 'rgba(56, 189, 248, 0.55)';
        ctx.beginPath();
        ctx.arc(t.startX, t.startY, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(t.endX, t.endY, 3, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    function pulsePosition(trace: Trace, progress: number) {
      const dist = progress * trace.totalLength;
      if (dist <= trace.length1) {
        const t = trace.length1 === 0 ? 0 : dist / trace.length1;
        return {
          x: trace.startX + (trace.cornerX - trace.startX) * t,
          y: trace.startY + (trace.cornerY - trace.startY) * t,
        };
      }
      const t = trace.length2 === 0 ? 0 : (dist - trace.length1) / trace.length2;
      return {
        x: trace.cornerX + (trace.endX - trace.cornerX) * t,
        y: trace.cornerY + (trace.endY - trace.cornerY) * t,
      };
    }

    function drawPulses() {
      pulses.forEach((p) => {
        const trace = traces[p.traceIndex];
        if (!trace) return;
        const { x, y } = pulsePosition(trace, p.progress);

        const halo = ctx.createRadialGradient(x, y, 0, x, y, 14);
        halo.addColorStop(0, 'rgba(125, 211, 252, 0.85)');
        halo.addColorStop(1, 'rgba(125, 211, 252, 0)');
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(x, y, 14, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = 'rgba(224, 242, 254, 1)';
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    function drawGear(g: Gear) {
      const innerRadius = g.radius * 0.55;
      const toothDepth = g.radius * 0.2;
      ctx.save();
      ctx.translate(g.x, g.y);
      ctx.rotate(g.rotation);
      ctx.fillStyle = 'rgba(56, 189, 248, 0.12)';
      ctx.beginPath();
      for (let i = 0; i < g.teeth; i++) {
        const angle = (Math.PI * 2 * i) / g.teeth;
        const next = (Math.PI * 2 * (i + 1)) / g.teeth;
        const mid = (angle + next) / 2;
        const halfTooth = (next - angle) / 4;
        ctx.lineTo(Math.cos(angle + halfTooth) * g.radius, Math.sin(angle + halfTooth) * g.radius);
        ctx.lineTo(
          Math.cos(angle + halfTooth) * (g.radius + toothDepth),
          Math.sin(angle + halfTooth) * (g.radius + toothDepth)
        );
        ctx.lineTo(
          Math.cos(mid + halfTooth) * (g.radius + toothDepth),
          Math.sin(mid + halfTooth) * (g.radius + toothDepth)
        );
        ctx.lineTo(Math.cos(mid + halfTooth) * g.radius, Math.sin(mid + halfTooth) * g.radius);
      }
      ctx.closePath();
      ctx.fill();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(0, 0, innerRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
      ctx.restore();
    }

    function drawGears() {
      gears.forEach((g) => drawGear(g));
    }

    function drawDigits() {
      ctx.font = '600 14px ui-monospace, "SFMono-Regular", Menlo, monospace';
      ctx.textAlign = 'center';
      digits.forEach((d) => {
        ctx.fillStyle = `rgba(125, 211, 252, ${d.alpha})`;
        ctx.font = `600 ${d.size}px ui-monospace, "SFMono-Regular", Menlo, monospace`;
        ctx.fillText(d.value, d.x, d.y);
      });
    }

    function step() {
      pulses.forEach((p) => {
        p.progress += p.speed;
        if (p.progress >= 1) {
          p.progress = 0;
          p.traceIndex = Math.floor(Math.random() * traces.length);
        }
      });
      gears.forEach((g) => {
        g.rotation += g.rotationSpeed;
      });
      digits.forEach((d) => {
        d.y -= d.speed;
        if (d.y < -20) {
          d.y = canvas.height + 20;
          d.x = Math.random() * canvas.width;
          d.value = Math.random() > 0.5 ? '1' : '0';
        }
      });
    }

    function renderStatic() {
      drawBase();
      drawGrid();
      drawGears();
      drawTraces();
      drawDigits();
    }

    function animate() {
      if (reducedMotion) return;

      drawBase();
      drawGrid();
      drawGears();
      drawTraces();
      drawPulses();
      drawDigits();

      step();

      animationFrameId.current = requestAnimationFrame(animate);
    }

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const handleReducedMotionChange = (event: MediaQueryListEvent) => {
      reducedMotion = event.matches;
      cancelAnimationFrame(animationFrameId.current);
      if (reducedMotion) {
        renderStatic();
      } else {
        animate();
      }
    };

    reducedMotionQuery?.addEventListener?.('change', handleReducedMotionChange);

    if (reducedMotion) {
      renderStatic();
    } else {
      animate();
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      reducedMotionQuery?.removeEventListener?.('change', handleReducedMotionChange);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
    />
  );
};

export default RobotBackground;
